import { useState } from "react";
import { toast } from "sonner";
import { FileDown, MessageCircle, Pencil, Receipt, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api, downloadFile, errMsg, fmtDate } from "@/lib/api";
import { COMPANY } from "@/lib/constants";
import { toWaNumber } from "@/components/ConfirmVisitDialog";

export const dmy = (iso) => (iso || "").split("-").reverse().join("/");

export const sar = (v) => Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DecisionBadge = ({ d }) => {
  const ok = d.status === "accepted";
  return (
    <div className={`mt-2 inline-flex flex-col rounded-lg px-3 py-1.5 text-sm font-bold ${ok ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`} data-testid="quote-decision-badge">
      <span className="flex items-center gap-1.5">{ok ? <CheckCircle2 size={15} /> : <XCircle size={15} />}{ok ? "قبل العميل العرض" : "رفض العميل العرض"} · <span className="font-normal text-xs">{fmtDate(d.at)}</span></span>
      {d.reason && <span className="font-normal text-xs">السبب: {d.reason}</span>}
    </div>
  );
};

export const QuoteCard = ({ insp, dark = false, onEdit }) => {
  const q = insp.quote;
  const [busy, setBusy] = useState("");

  const pdf = async () => {
    setBusy("pdf");
    try {
      await downloadFile(`/inspections/${insp.id}/quote/pdf`, `${q.number}.pdf`);
    } catch (_) {
      toast.error("تعذر تحميل عرض السعر");
    } finally {
      setBusy("");
    }
  };

  const send = async () => {
    const w = window.open("", "_blank");
    setBusy("wa");
    try {
      const [{ data: s }, { data: sh }] = await Promise.all([api.get("/settings"), api.post(`/inspections/${insp.id}/quote/share`)]);
      const n = toWaNumber(insp.customer.phone, s.country_code || "966");
      const msg = `مرحباً ${insp.customer.name}،\nنشكركم على إتاحة الفرصة لمعاينة موقعكم. مرفق عرض السعر المبدئي من ${COMPANY}:\nرقم العرض: ${q.number}\nالإجمالي شامل الضريبة: ${sar(q.total)} ر.س\nصالح حتى: ${dmy(q.valid_until)}\n\nللاطلاع على العرض والموافقة عليه أو رفضه:\n${window.location.origin}/q/${sh.token}`;
      const url = `https://wa.me/${n}?text=${encodeURIComponent(msg)}`;
      if (w) w.location.href = url;
      else window.location.href = url;
      if (!n) toast.info("رقم العميل غير مكتمل؛ اختر العميل من واتساب");
    } catch (e) {
      w?.close();
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const box = dark ? "border-brand-sky/50 bg-panel" : "border-slate-200 bg-white";
  const muted = dark ? "text-slate-400" : "text-slate-500";
  const btn = `h-11 px-4 rounded-lg font-bold text-sm flex items-center gap-1.5 transition-colors disabled:opacity-60`;
  return (
    <section className={`rounded-xl border p-4 sm:p-5 mb-5 ${box}`} data-testid="quote-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className={`text-xs font-bold flex items-center gap-1.5 ${dark ? "text-brand-sky" : "text-brand-navy"}`}><Receipt size={15} />عرض سعر مبدئي · <span className="font-mono">{q.number}</span>{q.version > 1 && ` (إصدار ${q.version})`}</p>
          <p className="font-heading text-2xl font-extrabold mt-1" data-testid="quote-total">{sar(q.total)} <span className="text-sm">ر.س</span></p>
          <p className={`text-xs ${muted}`}>{q.items.length} بند · قبل الضريبة {sar(q.subtotal)} · الضريبة {sar(q.vat)} · صالح حتى <span className="whitespace-nowrap">{dmy(q.valid_until)}</span></p>
          <p className={`text-[11px] ${muted}`}>صدر {fmtDate(q.issued_at)} بواسطة {q.issued_by}</p>
          {q.decision && <DecisionBadge d={q.decision} />}
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={pdf} disabled={!!busy} data-testid="quote-pdf-button" className={`${btn} ${dark ? "border border-line hover:bg-panel-hover" : "border border-slate-200 hover:bg-slate-100"}`}>
            {busy === "pdf" ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}PDF
          </button>
          <button onClick={send} disabled={!!busy} data-testid="quote-whatsapp-button" className={`${btn} bg-[#25D366] text-white hover:bg-[#1EB257]`}>
            {busy === "wa" ? <Loader2 size={16} className="animate-spin" /> : <MessageCircle size={16} />}إرسال للعميل
          </button>
          {onEdit && <button onClick={onEdit} data-testid="quote-edit-button" className={`${btn} border border-slate-200 hover:bg-slate-100`}><Pencil size={16} />تعديل</button>}
        </div>
      </div>
    </section>
  );
};
